/**
 * The IndexedDB adapter (decision 28: one record per chapter; decision 51: plaintext). Every write runs in one
 * read-write transaction that also covers the epoch record: the epoch is read first and every op is issued only
 * if it matches, so a tab that lost the lock can't commit (sync case table, "active tab"; part 06 log, tables W
 * and V). Transactions ask for strict durability, so a write reported committed survives a power cut.
 */
import { adopted } from './adopt.ts';
import { gatherBook } from './book-contents.ts';
import type { BookContents } from './book-contents.ts';
import { firstReadable, planImageAdd } from './image-add.ts';
import type { AddImageResult, ImageAddPlan } from './image-add.ts';
import { planMove } from './legacy-notes.ts';
import type { MoveInput, StoredRow } from './legacy-notes.ts';
import { BOOK_INDEXED_STORES, DB_VERSION, IMAGE_BYTES, IMAGES, ImportGoneError, KeyTakenError, MAX_EPOCH, NOTES_VERSION, readEpoch, readImage, readImageBytes, readRecord, STORE_INDEXES, STORE_NAMES, StoreRecordError } from './records.ts';
import type { ImageBytesRecord, ImageRecord, IndexedField, RecordOf, StoreName } from './records.ts';
import type { CreateResult, InvalidRecord, ReadAllResult, Store, StoreOp, WriteResult } from './store.types.ts';

/** The database every tab of the origin opens. */
export const DB_NAME = 'cockatrice';

/** The store holding the epoch record, under the key {@link EPOCH_KEY}. */
const META = 'meta';
const EPOCH_KEY = 'epoch';

/** Thrown when the database was upgraded by a later build (V4): this build must reload. */
export class StoreOutdatedError extends Error {
  override readonly name = 'StoreOutdatedError';

  constructor() {
    super('the local store was upgraded by a newer version of the app; reload to continue');
  }
}

/** What opening the database needs. */
export interface OpenOptions {
  /** `indexedDB`, or a stand-in. */
  readonly factory: IDBFactory;
  /** The database name; {@link DB_NAME} unless a test isolates itself. */
  readonly name?: string;
  /** Called when the upgrade waits for another tab to close its connection (V5). */
  readonly onBlocked?: () => void;
  /**
   * `IDBKeyRange`, or the stand-in's that goes with `factory` (part 16c: a book's images are read by its key range); the
   * global one when not given.
   */
  readonly keyRange?: Pick<typeof IDBKeyRange, 'bound'>;
  /** Part 19e, table MG: when the move to version 10 runs, in milliseconds since the Unix epoch; `Date.now` when not given. */
  readonly now?: () => number;
  /** Part 19e, MG5, MG8, MG11: a fresh ID for a moved note whose ID is taken; a random one when not given. */
  readonly newId?: () => string;
}

/** A random ID (decision 37: 128 bits as 32 lowercase hex digits). */
function randomId(): string {
  return [...crypto.getRandomValues(new Uint8Array(16))].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Part 19e, table MG: inside the upgrade's transaction, reads every record of the three stores the notes move from, then
 * writes the plan ({@link planMove}): the note records, the entries without their notes, and the moved records deleted.
 * Everything is a request of the same transaction, so the move commits whole with the upgrade or not at all (MG13).
 */
function moveNotes(transaction: IDBTransaction, at: number, newId: () => string): void {
  const rows: Record<keyof MoveInput, StoredRow[]> = { comments: [], bookNotes: [], entries: [] };
  let pending = 3;
  for (const name of ['comments', 'bookNotes', 'entries'] as const) {
    const store = transaction.objectStore(name);
    const keys = store.getAllKeys();
    const values = store.getAll();
    // Both requests read the same store in one transaction, in key order, so the n-th key is the n-th value's.
    values.onsuccess = () => {
      rows[name] = keys.result.map((key, index): StoredRow => [key, values.result[index]]);
      pending -= 1;
      if (pending > 0) return;
      const plan = planMove(rows, at, newId);
      const notes = transaction.objectStore('notes');
      for (const note of plan.notes) notes.put(note);
      const entries = transaction.objectStore('entries');
      for (const entry of plan.entries) entries.put(entry);
      for (const key of plan.comments) transaction.objectStore('comments').delete(key);
      for (const key of plan.bookNotes) transaction.objectStore('bookNotes').delete(key);
    };
  }
}

/**
 * Opens the local store, creating missing object stores and never deleting one (V1, V2). Rejects with
 * {@link StoreOutdatedError} when a later build has upgraded the database (V4), or with the browser's error.
 * `version` is this build's {@link DB_VERSION}; tests pass another to play an earlier or later build.
 */
export function openIndexedDbStore(options: OpenOptions, version: number = DB_VERSION): Promise<Store> {
  return new Promise((resolve, reject) => {
    const request = options.factory.open(options.name ?? DB_NAME, version);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
      // Missing stores are created, and missing indexes added to every store, new or old (V2).
      for (const name of STORE_NAMES) {
        const objects = db.objectStoreNames.contains(name) ? request.transaction?.objectStore(name) : db.createObjectStore(name, { keyPath: 'id' });
        const indexes: readonly string[] = STORE_INDEXES[name];
        for (const field of indexes) if (objects?.indexNames.contains(field) === false) objects.createIndex(field, field);
      }
      // Part 16a (table U): images and their bytes, each under its book and ID.
      const images = db.objectStoreNames.contains(IMAGES) ? request.transaction?.objectStore(IMAGES) : db.createObjectStore(IMAGES, { keyPath: ['bookId', 'id'] });
      if (images !== undefined) {
        if (!images.indexNames.contains('bookId')) images.createIndex('bookId', 'bookId');
        if (!images.indexNames.contains('bookHash')) images.createIndex('bookHash', ['bookId', 'sha256']);
      }
      if (!db.objectStoreNames.contains(IMAGE_BYTES)) db.createObjectStore(IMAGE_BYTES, { keyPath: ['bookId', 'id'] });
      // Part 19e, table MG: a database an earlier version made has its notes moved, once (MG15: a new one has none).
      const { transaction } = request;
      if (event.oldVersion > 0 && event.oldVersion < NOTES_VERSION && version >= NOTES_VERSION && transaction !== null) {
        moveNotes(transaction, (options.now ?? Date.now)(), options.newId ?? randomId);
      }
    };
    request.onblocked = () => {
      options.onBlocked?.();
    };
    request.onerror = () => {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- the browser's own error, passed through unchanged
      reject(request.error?.name === 'VersionError' ? new StoreOutdatedError() : request.error);
    };
    request.onsuccess = () => {
      resolve(new IndexedDbStore(request.result, options.keyRange ?? globalThis.IDBKeyRange));
    };
  });
}

/** Resolves with `value` when `transaction` completes, and with the error it aborted with otherwise. */
function finish<T>(transaction: IDBTransaction, value: () => T): Promise<T> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve(value());
    };
    transaction.onabort = () => {
      reject(transaction.error ?? new Error('the transaction was aborted'));
    };
  });
}

/** The object stores `op` touches: an image's two, every store a book is kept in for an abandon (S11). */
function storesOf(op: StoreOp): string[] {
  switch (op.kind) {
    case 'add-image':
      return [IMAGES, IMAGE_BYTES];
    case 'abandon-import':
    case 'adopt-import':
      return [...STORE_NAMES, IMAGES, IMAGE_BYTES];
    case 'put':
    case 'add':
    case 'delete':
      return [op.store];
  }
}

/** What a write's request callbacks found, read once its transaction ends. */
class WriteOutcome {
  stale = false;
  failure: unknown = undefined;
}

/** What a create's request callbacks found. */
class CreateOutcome {
  exists = false;
}

class IndexedDbStore implements Store {
  private readonly db: IDBDatabase;
  private readonly keyRange: Pick<typeof IDBKeyRange, 'bound'>;
  private readonly listeners: (() => Promise<void>)[] = [];
  private readonly closeListeners: (() => void)[] = [];

  constructor(db: IDBDatabase, keyRange: Pick<typeof IDBKeyRange, 'bound'>) {
    this.db = db;
    this.keyRange = keyRange;
    db.onclose = () => {
      for (const listener of this.closeListeners) listener();
    };
    db.onversionchange = () => {
      void Promise.allSettled(this.listeners.map((listener) => listener())).then(() => {
        db.close();
      });
    };
  }

  async read<S extends StoreName>(store: S, id: string): Promise<RecordOf<S> | undefined> {
    const transaction = this.db.transaction(store, 'readonly');
    const request = transaction.objectStore(store).get(id);
    const raw: unknown = await finish(transaction, (): unknown => request.result);
    if (raw === undefined) return undefined;
    return readRecord(store, raw);
  }

  async readAll<S extends StoreName>(store: S): Promise<ReadAllResult<S>> {
    const transaction = this.db.transaction(store, 'readonly');
    return await this.collect(store, transaction, transaction.objectStore(store));
  }

  async readWhere<S extends StoreName>(store: S, field: IndexedField<S>, value: string): Promise<ReadAllResult<S>> {
    const transaction = this.db.transaction(store, 'readonly');
    return await this.collect(store, transaction, transaction.objectStore(store).index(field), value);
  }

  /** Every record `source` holds (under `query`), read with the store's reader (R4). */
  private async collect<S extends StoreName>(store: S, transaction: IDBTransaction, source: IDBObjectStore | IDBIndex, query?: string): Promise<ReadAllResult<S>> {
    const values = source.getAll(query);
    const keys = source.getAllKeys(query);
    const { raw, stored } = await finish(transaction, () => {
      const all: readonly unknown[] = values.result;
      return { raw: all, stored: keys.result };
    });
    const records: RecordOf<S>[] = [];
    const invalid: InvalidRecord[] = [];
    raw.forEach((value, index) => {
      const key = stored[index];
      try {
        records.push(readRecord(store, value));
      } catch (error) {
        invalid.push({ key, error });
      }
    });
    return { records, invalid };
  }

  async readBook(bookId: string): Promise<BookContents> {
    // One readonly transaction over every store a book's records are in: each request is made in the callback of the one
    // naming its record, so the transaction stays open until the last is in, and a write waits or went first (table R).
    // Part 16c: the images and their bytes too, so an add (one transaction over both) is wholly in or wholly out; part
    // 19d: the book notes, so a note's add or write is too; part 23c: the comments, so a re-import's are; part 38d: the
    // reading links; part 38b: the shares.
    const transaction = this.db.transaction(['books', 'entries', 'chapters', 'conflictCopies', 'snapshots', 'snapshotTexts', 'bookNotes', 'comments', 'notes', 'shareLinks', 'shares', IMAGES, IMAGE_BYTES], 'readonly');
    const finishBook = gatherBook(bookId, {
      get: (store, key, then) => {
        const request = transaction.objectStore(store).get(key);
        request.onsuccess = () => {
          then(request.result);
        };
      },
      where: (store, field, value, then) => {
        const index = transaction.objectStore(store).index(field);
        const values = index.getAll(value);
        const keys = index.getAllKeys(value);
        // Requests on one transaction complete in the order made: the values are in when the keys are.
        keys.onsuccess = () => {
          const all: readonly unknown[] = values.result;
          then(keys.result.map((key, i): readonly [unknown, unknown] => [key, all[i]]));
        };
      },
      images: (store, book, then) => {
        const range = this.bookRange(book);
        const source = transaction.objectStore(store);
        const values = source.getAll(range);
        const keys = source.getAllKeys(range);
        keys.onsuccess = () => {
          const all: readonly unknown[] = values.result;
          // Each key in the range is an array, `[book, id]`: its second element is the ID part.
          then(keys.result.map((key, i): readonly [unknown, unknown] => [Reflect.get(Object(key), 1), all[i]]));
        };
      },
    });
    return await finish(transaction, finishBook);
  }

  async hasBook(bookId: string): Promise<boolean> {
    // One readonly transaction; each count is by key (HB1, HB9), by a `bookId` index (HB2–HB7) or by the book's key range
    // (HB8).
    const transaction = this.db.transaction(['books', 'imports', ...BOOK_INDEXED_STORES, IMAGES, IMAGE_BYTES], 'readonly');
    const range = this.bookRange(bookId);
    const counts = [
      transaction.objectStore('books').count(bookId),
      transaction.objectStore('imports').count(bookId),
      ...BOOK_INDEXED_STORES.map((name) => transaction.objectStore(name).index('bookId').count(bookId)),
      transaction.objectStore(IMAGES).count(range),
      transaction.objectStore(IMAGE_BYTES).count(range),
    ];
    return await finish(transaction, () => counts.some((request) => request.result > 0));
  }

  /** Every `[book, …]` key: arrays order by their first element, and no text lies between `book` and `book` + U+0000. */
  private bookRange(bookId: string): IDBKeyRange {
    return this.keyRange.bound([bookId], [`${bookId}\u0000`], false, true);
  }

  async readEpoch(): Promise<number> {
    const transaction = this.db.transaction(META, 'readonly');
    const request = transaction.objectStore(META).get(EPOCH_KEY);
    return readEpoch(await finish(transaction, (): unknown => request.result));
  }

  async bumpEpoch(): Promise<number> {
    const transaction = this.db.transaction(META, 'readwrite', { durability: 'strict' });
    const meta = transaction.objectStore(META);
    const request = meta.get(EPOCH_KEY);
    let next = 0;
    let abortWith: unknown = undefined;
    request.onsuccess = () => {
      try {
        const epoch = readEpoch(request.result);
        if (epoch >= MAX_EPOCH) throw new StoreRecordError('epoch', 'is at its largest value');
        next = epoch + 1;
        meta.put(next, EPOCH_KEY);
      } catch (error) {
        abortWith = error;
        transaction.abort();
      }
    };
    try {
      return await finish(transaction, () => next);
    } catch (error) {
      throw abortWith ?? error;
    }
  }

  async write(epoch: number, ops: readonly StoreOp[]): Promise<WriteResult> {
    for (const op of ops) {
      if (op.kind === 'put' || op.kind === 'add') readRecord(op.store, op.value);
      if (op.kind === 'add-image') {
        readImage(op.image);
        readImageBytes(op.bytes);
      }
    }
    const outcome = new WriteOutcome();
    try {
      const stores = new Set<string>([META, ...ops.flatMap(storesOf)]);
      const transaction = this.db.transaction([...stores], 'readwrite', { durability: 'strict' });
      /** Principle 2: a create that meets a held key fails the write with what was taken (the transaction aborts itself). */
      const create = (store: string, value: unknown): void => {
        const request = transaction.objectStore(store).add(value);
        request.onerror = () => {
          if (request.error?.name === 'ConstraintError') outcome.failure ??= new KeyTakenError(store);
        };
      };
      /** Issues the ops from `index` on, in order: an abandon's deletes go out from its reads' callback, and the rest after them. */
      const apply = (index: number): void => {
        for (const [offset, op] of ops.slice(index).entries()) {
          switch (op.kind) {
            case 'put':
              transaction.objectStore(op.store).put(op.value);
              break;
            case 'add':
              create(op.store, op.value);
              break;
            case 'delete':
              transaction.objectStore(op.store).delete(op.id);
              break;
            case 'add-image':
              create(IMAGES, op.image);
              create(IMAGE_BYTES, op.bytes);
              break;
            case 'abandon-import':
              this.abandon(transaction, op.bookId, () => {
                apply(index + offset + 1);
              });
              return;
            case 'adopt-import':
              this.adopt(transaction, op, create, (error) => {
                outcome.failure = error;
                transaction.abort();
              }, () => {
                apply(index + offset + 1);
              });
              return;
          }
        }
      };
      const request = transaction.objectStore(META).get(EPOCH_KEY);
      request.onsuccess = () => {
        try {
          if (readEpoch(request.result) !== epoch) {
            outcome.stale = true;
            transaction.abort();
            return;
          }
          apply(0);
        } catch (error) {
          outcome.failure = error;
          transaction.abort();
        }
      };
      await finish(transaction, () => undefined);
      return { kind: 'committed' };
    } catch (error) {
      if (outcome.stale) return { kind: 'stale' };
      return { kind: 'failed', error: outcome.failure ?? error };
    }
  }

  /**
   * S11, principle 1: in `transaction`, when book `bookId` has a pending import, deletes its record, every record its
   * `bookId` indexes find, the texts of its snapshots, its images and their bytes, and the marker; then calls `then`.
   */
  private abandon(transaction: IDBTransaction, bookId: string, then: () => void): void {
    const marker = transaction.objectStore('imports').count(bookId);
    marker.onsuccess = () => {
      if (marker.result === 0) {
        then();
        return;
      }
      const found = BOOK_INDEXED_STORES.map((name) => [name, transaction.objectStore(name).index('bookId').getAllKeys(bookId)] as const);
      let left = found.length;
      const deleteAll = (): void => {
        for (const [name, request] of found) {
          for (const key of request.result) {
            transaction.objectStore(name).delete(key);
            if (name === 'snapshots') transaction.objectStore('snapshotTexts').delete(key);
          }
        }
        const range = this.bookRange(bookId);
        transaction.objectStore(IMAGES).delete(range);
        transaction.objectStore(IMAGE_BYTES).delete(range);
        transaction.objectStore('books').delete(bookId);
        transaction.objectStore('imports').delete(bookId);
        then();
      };
      for (const [, request] of found) {
        request.onsuccess = () => {
          left -= 1;
          if (left === 0) deleteAll();
        };
      }
    };
  }

  /**
   * Part 24d, S′5, S′11: in `transaction`, hands the pending import of book `op.from` to book `op.to`: every record its
   * `bookId` indexes find put again naming `op.to` (its `chapterId` mapped by `op.chapters`), its images and their bytes
   * created under `op.to` (`create`, so a held key fails the write) one at a time by cursor and deleted where they were,
   * and the marker deleted; then calls `then`. With no marker, `fail` gets an {@link ImportGoneError}.
   */
  private adopt(
    transaction: IDBTransaction,
    op: Extract<StoreOp, { readonly kind: 'adopt-import' }>,
    create: (store: string, value: unknown) => void,
    fail: (error: unknown) => void,
    then: () => void,
  ): void {
    const chapters = new Map(op.chapters);
    const marker = transaction.objectStore('imports').count(op.from);
    marker.onsuccess = () => {
      if (marker.result === 0) {
        fail(new ImportGoneError(op.from));
        return;
      }
      const found = BOOK_INDEXED_STORES.map((name) => [name, transaction.objectStore(name).index('bookId').getAll(op.from)] as const);
      let left = found.length;
      /** Moves every record of `store` in the staging book's key range, one at a time, then calls `next`. */
      const move = (store: string, next: () => void): void => {
        const cursor = transaction.objectStore(store).openCursor(this.bookRange(op.from));
        cursor.onsuccess = () => {
          const at = cursor.result;
          if (at === null) {
            next();
            return;
          }
          create(store, adopted(at.value, op.to, chapters));
          at.delete();
          at.continue();
        };
      };
      const putAll = (): void => {
        for (const [name, request] of found) for (const value of request.result) transaction.objectStore(name).put(adopted(value, op.to, chapters));
        move(IMAGES, () => {
          move(IMAGE_BYTES, () => {
            transaction.objectStore('imports').delete(op.from);
            then();
          });
        });
      };
      for (const [, request] of found) {
        request.onsuccess = () => {
          left -= 1;
          if (left === 0) putAll();
        };
      }
    };
  }

  async create<S extends StoreName>(store: S, value: RecordOf<S>): Promise<CreateResult> {
    readRecord(store, value);
    const outcome = new CreateOutcome();
    try {
      const transaction = this.db.transaction(store, 'readwrite', { durability: 'strict' });
      const request = transaction.objectStore(store).add(value);
      request.onerror = () => {
        outcome.exists = request.error?.name === 'ConstraintError';
      };
      await finish(transaction, () => undefined);
      return { kind: 'created' };
    } catch (error) {
      if (outcome.exists) return { kind: 'exists' };
      return { kind: 'failed', error };
    }
  }

  async addImage(image: ImageRecord, bytes: Uint8Array, cap: number): Promise<AddImageResult> {
    readImage(image);
    readImageBytes({ bookId: image.bookId, id: image.id, bytes });
    try {
      const transaction = this.db.transaction([IMAGES, IMAGE_BYTES], 'readwrite', { durability: 'strict' });
      const images = transaction.objectStore(IMAGES);
      const stored = transaction.objectStore(IMAGE_BYTES);
      // Every read and the write in one transaction, each request made in the callback before it (A10, A11).
      const planned = new Promise<ImageAddPlan>((resolve) => {
        const sameHash = images.index('bookHash').getAll([image.bookId, image.sha256]);
        const record = images.get([image.bookId, image.id]);
        const free: IDBRequest<unknown> = stored.get([image.bookId, image.id]);
        const book = images.index('bookId').getAll(image.bookId);
        // Requests on one transaction complete in the order made: the others are in when the book's are.
        book.onsuccess = () => {
          const decide = (sameBytes: unknown): void => {
            const plan = planImageAdd(image, bytes, cap, {
              sameHash: sameHash.result,
              record: record.result,
              bytes: (id) => (id === image.id ? free.result : sameBytes),
              book: book.result,
            });
            if (plan.write !== null) {
              images.put(plan.write.image);
              stored.put(plan.write.bytes);
            }
            resolve(plan);
          };
          const same = firstReadable(sameHash.result);
          if (same === undefined) decide(undefined);
          else {
            const request = stored.get([image.bookId, same.id]);
            request.onsuccess = () => {
              decide(request.result);
            };
          }
        };
      });
      const [plan] = await Promise.all([planned, finish(transaction, () => undefined)]);
      return plan.result;
    } catch (error) {
      return { kind: 'failed', error };
    }
  }

  async readImages(bookId: string): Promise<{ readonly records: readonly ImageRecord[]; readonly invalid: readonly InvalidRecord[] }> {
    const transaction = this.db.transaction(IMAGES, 'readonly');
    const source = transaction.objectStore(IMAGES);
    const range = this.bookRange(bookId);
    const values = source.getAll(range);
    const keys = source.getAllKeys(range);
    await finish(transaction, () => undefined);
    const records: ImageRecord[] = [];
    const invalid: InvalidRecord[] = [];
    const all: readonly unknown[] = values.result;
    keys.result.forEach((key, i) => {
      try {
        records.push(readImage(all[i]));
      } catch (error) {
        // Each key in the range is an array, `[book, id]`: its second element is the ID.
        invalid.push({ key: Reflect.get(Object(key), 1), error });
      }
    });
    return { records, invalid };
  }

  async readImage(bookId: string, id: string): Promise<ImageRecord | undefined> {
    const transaction = this.db.transaction(IMAGES, 'readonly');
    const request = transaction.objectStore(IMAGES).get([bookId, id]);
    const raw: unknown = await finish(transaction, (): unknown => request.result);
    return raw === undefined ? undefined : readImage(raw);
  }

  async findImage(bookId: string, sha256: string): Promise<ImageRecord | undefined> {
    const transaction = this.db.transaction(IMAGES, 'readonly');
    const request = transaction.objectStore(IMAGES).index('bookHash').getAll([bookId, sha256]);
    return firstReadable(await finish(transaction, (): unknown[] => request.result));
  }

  async readImageBytes(bookId: string, id: string): Promise<ImageBytesRecord | undefined> {
    const transaction = this.db.transaction(IMAGE_BYTES, 'readonly');
    const request = transaction.objectStore(IMAGE_BYTES).get([bookId, id]);
    const raw: unknown = await finish(transaction, (): unknown => request.result);
    return raw === undefined ? undefined : readImageBytes(raw);
  }

  onVersionChange(listener: () => Promise<void>): void {
    this.listeners.push(listener);
  }

  onClose(listener: () => void): void {
    this.closeListeners.push(listener);
  }

  close(): void {
    this.db.close();
  }
}
